import React, { useState, useEffect, ReactNode } from 'react';
import { useMobileChart } from '../../context/MobileChartContext';
import ResponsiveChartWrapper from './ResponsiveChartWrapper';

interface ResponsiveChartProps {
  title: string;
  children: ReactNode;
  height?: string;
  className?: string;
  withExpandButton?: boolean;
}

/**
 * A component that makes any chart responsive across different devices
 * 
 * Features:
 * - Provides proper sizing for mobile and desktop
 * - Handles chart expansion and mobile overlay automatically
 * - Adds visual indicators like "Tap to expand" on mobile
 * 
 * @example
 * <ResponsiveChart title="Revenue Trend">
 *   <LineChart data={revenueData} />
 * </ResponsiveChart>
 */
const ResponsiveChart: React.FC<ResponsiveChartProps> = ({
  title,
  children,
  height = '300px',
  className = '',
  withExpandButton = true
}) => {
  const { isMobile } = useMobileChart();
  const [chartRef, setChartRef] = useState<any>({ current: { node: null } });
  const [isMounted, setIsMounted] = useState(false);
  
  // Check if component is mounted (for SSR compatibility)
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Safe function to open the modal
  const handleExpandClick = () => {
    if (chartRef?.current?.openModal) {
      chartRef.current.openModal();
    }
  };
  
  // If not mounted yet, render a placeholder with the same height
  if (!isMounted) {
    return <div style={{ height }} className={className} />;
  }
  
  return (
    <div className={`relative ${className}`} style={{ height }}>
      <ResponsiveChartWrapper 
        title={title}
        className="h-full"
        setChartRef={setChartRef}
      >
        {children}
      </ResponsiveChartWrapper>
      
      {/* Expand button (desktop only) */}
      {!isMobile && withExpandButton && (
        <button
          onClick={handleExpandClick}
          className="absolute bottom-4 right-4 bg-white/90 hover:bg-white text-slate-700 hover:text-slate-900 font-medium text-xs px-2.5 py-1 rounded border border-slate-200 shadow-sm flex items-center gap-1"
          title="Expand Chart"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9"></polyline>
            <polyline points="9 21 3 21 3 15"></polyline>
            <line x1="21" y1="3" x2="14" y2="10"></line>
            <line x1="3" y1="21" x2="10" y2="14"></line>
          </svg>
          Expand
        </button>
      )}
    </div>
  );
};

export default ResponsiveChart; 